/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useEffect, useState } from 'react';
import {
    string, shape, number, object,
} from 'prop-types';
import loadable from '@loadable/component';
import mbpLogger from '@module-logger/mbp';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';

import ComponentErrorBoundary from '../../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import TrendingProductRecsBuilder from './TrendingProductRecsBuilder';
import { getSalesforceAttributes } from '../../../../../state/ducks/App/App-Selectors';
import CarouselBuilderSkeleton from '../../GraphqlSkeletonComponents/CarouselBuilderSkeleton';
import { TabTrendingProductSkeleton } from '../../GraphqlSkeletonComponents/TabTrendingProductSkeleton';
import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import findCategoryById from '../../../../gql/queries/findCategoryById';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { GRAPHQL_ENV } from '../../../../gql';
import useExperimentServiceAttributes from '../../../../helpers/experimentService/useExperimentServiceAttributes';
import useTrendingProductsQuery from '../../../../helpers/Personalization/useTrendingProductQuery';
import {
    buildTrendingProductEvent,
    getTrendingProductName,
    TRENDING_GIFTS_BANNER_GROUP,
} from '../../EnterpriseID/Common/trendingTabsTracking';
import { setActiveBannerProps } from '../../../../helpers/tracking/banner/activeBannerHelpers';

const LoadableSimpleCarouselBuilder = loadable(() => import(/* webpackChunkName: "SimpleCarouselBuilder" */ '../../EnterpriseID/Common/SimpleCarouselBuilder'));

const normalizeQueryData = (data, hasCategoryId) => {
    if (!data) return null;
    if (hasCategoryId && data.findCategoryById) {
        return { products: data.findCategoryById?.products, categoryData: data.findCategoryById };
    }
    return {
        products: data.trendingProducts?.topProducts?.map((item) => item.product) || [],
    };
};

// CMS sends a comma separated string for a single entry, otherwise an array of entries
const parseExcludedProducts = (excludedProducts) => [].concat(excludedProducts || [])
    .flatMap((entry) => String(entry).split(','))
    .map((id) => id.trim())
    .filter(Boolean);

const TrendingProductsABContainer = ({
    content, layoutDisplayType,
}) => {
    const dispatch = useDispatch();
    const ffIsTrendingUxUpdateEnabled = useSelector(getFeatureFlag('is-trending-ux-update-enabled'));
    const brandName = useSelector(getBrandName);
    const salesforceAttributes = useSelector(getSalesforceAttributes);

    const hasCategoryId = salesforceAttributes?.categoryTabs?.categoryId?.length > 0;
    const categoryId = salesforceAttributes?.categoryTabs?.categoryId;
    const trendingFilters = content?.trending_filters;
    const personalizedCarousel = content?.personalized_carousel;
    const productsToShow = personalizedCarousel?.products_to_show;
    const gaTagging = content?.ga_tagging;
    const navPillsCampaignId = content?.tabs_ref?.[0] || null;

    const { targeting, context, isGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findCategoryById' });

    // Always call both hooks - use skip to disable the one we don't need
    const { loading, error, data } = useQuery(
        findCategoryById(isGraphqlTargetingEnabled, context?.api === 'experiments'),
        {
            skip: !hasCategoryId,
            variables: {
                brand: brandName,
                environment: GRAPHQL_ENV,
                locale: content?.locale || 'en-us',
                id: categoryId || '',
                productOptions: {
                    pageSize: trendingFilters?.page_size || 10,
                    page: 1,
                },
                ...(isGraphqlTargetingEnabled ? { targeting } : {}),
            },
            context,
        },
    );

    const excludeProductIds = parseExcludedProducts(trendingFilters?.excluded_products);

    const {
        loading: loading1, error: error1, data: data1,
    } = useTrendingProductsQuery({
        queryName: 'TrendingProducts',
        pageSize: trendingFilters?.page_size || 10,
        dateRange: trendingFilters?.date_range,
        filters: {
            productCategoryHierarchy: salesforceAttributes?.categoryTabs?.referenceProductCategory || '',
            brandCode: trendingFilters?.brand_code || '',
            excludeSympathy: trendingFilters?.exclude_sympathy,
            useLastCategoryViewed: trendingFilters?.last_category_viewed,
            ...(excludeProductIds.length ? { excludeProductIds } : {}),
        },
        featureFlag: !hasCategoryId,
    });

    const trendingData = normalizeQueryData(hasCategoryId ? data : data1, hasCategoryId);

    const previousDataRef = useRef(null);
    useEffect(() => {
        if (trendingData?.products) {
            previousDataRef.current = trendingData;
        }
    }, [trendingData]);

    if (((loading || loading1) && !previousDataRef.current)) {
        if (navPillsCampaignId) {
            return (
                <TabTrendingProductSkeleton
                    useNavPillSkeleton={ffIsTrendingUxUpdateEnabled}
                    nDisplayProducts={productsToShow}
                    breakPoints={{ mobile: 601, desktop: 769 }}
                />
            );
        }
        return (
            <CarouselBuilderSkeleton
                nDisplayProducts={productsToShow}
                breakPoints={{ mobile: 601, desktop: 769 }}
            />
        );
    }

    if (error || error1) {
        mbpLogger.logError({
            function: 'TrendingProductsABContainer.js',
            message: 'Error loading data from trending products api',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error || error1,
        });
    }

    const activeData = trendingData || previousDataRef?.current;

    const productClickCallback = (partNumber) => {
        const productName = getTrendingProductName(activeData?.products, partNumber);

        const bannerType = 'Trending Tab';
        const bannerGroup = gaTagging?.event_action || TRENDING_GIFTS_BANNER_GROUP || 'Trending Gifts';
        const bannerItemName = productName || '';

        setActiveBannerProps(bannerType, bannerGroup, bannerItemName);

        // call GA Events
        dispatch(trackEvent({
            eventCategory: gaTagging?.event_category || 'Homepage',
            eventAction: gaTagging?.event_action || 'Trending Gifts',
            eventLabel: productName,
            feature_category: gaTagging?.event_category || 'Homepage',
            feature_element: gaTagging?.event_action || 'Trending Gifts',
            feature_detail: productName,
        }));
        dispatch(trackEvent(buildTrendingProductEvent({
            productName,
            partNumber,
        })));
    };

    if (Number(content?.minimum_products_to_show) === 0) return null;

    if (activeData?.products?.length) {
        const applyNewProductCardDesign = content?.personalized_product_card_reference?.length > 0;

        if (applyNewProductCardDesign) {
            return (
                <TrendingProductRecsBuilder
                    data={activeData}
                    products_to_show={productsToShow}
                    recommendationsType="productRecommendationsSFMulti"
                    productClickCallback={productClickCallback}
                    isLoading={loading || loading1} // products are loading
                    content={content}
                    layoutDisplayType={layoutDisplayType}
                />
            );
        }

        const heading = personalizedCarousel?.heading?.[0] || {};
        const attributionToken = trendingData?.categoryData?.attributionToken || '';

        return (
            <ComponentErrorBoundary function="TrendingProductsABContainer" module="PersonalizedContent">
                <LoadableSimpleCarouselBuilder
                    blockData={{
                        productsToShow,
                        title: heading?.copy || 'Trending Products For You',
                        titleFontColor: heading?.color?.color,
                        titleFontSize: heading?.font_size,
                        titleFontStyle: heading?.font_style,
                        titleFontFamily: heading?.font_family,
                        titleFontWeight: heading?.font_weight,
                        titleMargin: heading?.margin,
                        alignTitle: heading?.align_title,
                        padding: personalizedCarousel?.padding,
                    }}
                    products={activeData?.products || []}
                    productClickCallback={productClickCallback}
                    demandType="XS"
                    isLoading={loading || loading1}// products are loading
                    navPillsCampaignId={navPillsCampaignId}
                    attributionToken={attributionToken}
                    dataTestId="homepage-trending-products"
                />
            </ComponentErrorBoundary>
        );
    }

    return null;
};

TrendingProductsABContainer.propTypes = {
    content: object.isRequired,
    layoutDisplayType: string.isRequired,
};

export default TrendingProductsABContainer;
